/**
 * /app/resolutions/$id/sign — legacy bookmark redirector (SIGNER-07).
 *
 * The canonical signing surface is trust-scoped:
 *   /app/trusts/$id/resolutions/$rid/sign
 * Older links / bookmarks point at the flat /app/resolutions/$id/sign path
 * which carries the resolution id but not its trust id.
 *
 * Previously this route bounced UNCONDITIONALLY to the resolutions picker with
 * no context — the user landed on a list with no idea why. We now resolve the
 * owning trust via GET /v1/resolutions/{id} (which returns `trustId`, gated by
 * the caller's own session — APIM + verifyJwt) and forward to the real signing
 * screen. Only when that lookup fails (deleted resolution, no access, network)
 * do we fall back to the picker — the same safe landing as before, but now the
 * common case lands the user exactly where they meant to go.
 */

import { createFileRoute, redirect } from '@tanstack/react-router';
import { api } from '../../../../lib/api/client';

interface ResolutionTrustLookup {
  resolution?: { trustId?: string };
}

export const Route = createFileRoute('/app/resolutions/$id/sign')({
  beforeLoad: async ({ params }) => {
    const resolutionId = params.id;
    let trustId: string | undefined;
    try {
      const res = await api<ResolutionTrustLookup>(
        `/api/v1/resolutions/${encodeURIComponent(resolutionId)}`,
      );
      trustId = res.resolution?.trustId;
    } catch {
      // Lookup failed (deleted / no access / network) — fall back to the
      // picker below rather than dead-ending.
      trustId = undefined;
    }
    if (trustId) {
      throw redirect({
        to: '/app/trusts/$id/resolutions/$rid/sign',
        params: { id: trustId, rid: resolutionId },
      });
    }
    throw redirect({ to: '/app/resolutions' });
  },
  component: () => null,
});
